import type { ErrorLogDetail } from '@atlas-control/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/page-header';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { guard } from '@/lib/guards';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/errors/$errorId')({
  beforeLoad: ({ context }) => guard(context.me, 'errors.read'),
  component: ErrorPage,
});

function ErrorPage() {
  const { errorId } = Route.useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['errors', 'detail', errorId],
    queryFn: () => api<{ error: ErrorLogDetail }>(`/errors/${errorId}`).then((r) => r.error),
  });
  const [note, setNote] = useState('');
  const [all, setAll] = useState(true);
  const onDone = (r: { error: ErrorLogDetail }) => {
    qc.setQueryData(['errors', 'detail', errorId], r.error);
    void qc.invalidateQueries({ queryKey: ['errors'] });
  };
  const resolve = useMutation({
    mutationFn: () =>
      api<{ error: ErrorLogDetail }>(`/errors/${errorId}/resolve`, {
        method: 'POST',
        body: { note: note || undefined, allOccurrences: all },
      }),
    onSuccess: (r) => {
      onDone(r);
      toastSuccess('Marked resolved');
    },
    onError: (e) => toastError(e),
  });
  const reopen = useMutation({
    mutationFn: () =>
      api<{ error: ErrorLogDetail }>(`/errors/${errorId}/reopen`, { method: 'POST' }),
    onSuccess: onDone,
    onError: (e) => toastError(e),
  });

  const e = q.data;
  if (!e) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const facts: [string, React.ReactNode][] = [
    ['When', formatDateTime(e.occurredAt)],
    ['Level / source', `${e.level} · ${e.source}`],
    ['Request id', e.requestId ? <code className="select-all">{e.requestId}</code> : '-'],
    ['Route', e.path ? `${e.httpMethod ?? ''} ${e.path} → ${e.statusCode ?? ''}` : '-'],
    ['Job', e.jobName ? `${e.jobName} (${e.jobId ?? ''})` : '-'],
    ['User', e.user ? `${e.user.name ?? e.user.id} · ${e.user.role ?? ''}` : '-'],
    [
      'Run',
      e.runId ? (
        <Link to="/runs/$runId" params={{ runId: e.runId }}>
          {e.runId}
        </Link>
      ) : (
        '-'
      ),
    ],
    [
      'Form',
      e.formId ? (
        <Link to="/forms/$formId" params={{ formId: e.formId }}>
          {e.formId}
        </Link>
      ) : (
        '-'
      ),
    ],
    ['Atlas run', e.atlasRunId ?? '-'],
    ['Version / host', `${e.appVersion ?? '-'} · ${e.host ?? '-'}`],
    [
      'Occurrences',
      `${e.occurrences.count} (first ${formatDateTime(e.occurrences.firstSeen)}, last ${formatDateTime(e.occurrences.lastSeen)})`,
    ],
  ];

  return (
    <>
      <PageHeader
        title={<span className="font-mono">{e.code}</span>}
        description={e.message}
        actions={
          e.resolvedAt ? (
            <StatusBadge tone="success">Resolved</StatusBadge>
          ) : (
            <StatusBadge tone="danger">Open</StatusBadge>
          )
        }
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardContent>
              <dl className="grid grid-cols-[9rem_1fr] gap-y-2 text-sm">
                {facts.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="break-all">{v}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
          <Block title="Stack" value={e.stack} />
          <Block title="Cause" value={e.cause} />
          <Block title="Context (redacted)" value={e.context} />
          {e.userAgent && <Block title="User agent" value={e.userAgent} />}
        </div>
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-base">Triage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {e.resolvedAt ? (
              <>
                <p>
                  Resolved by {e.resolvedBy?.name ?? '-'} · {formatDateTime(e.resolvedAt)}
                </p>
                {e.resolutionNote && (
                  <p className="rounded bg-muted p-2 whitespace-pre-wrap">{e.resolutionNote}</p>
                )}
                <Button
                  variant="outline"
                  onClick={() => reopen.mutate()}
                  disabled={reopen.isPending}
                >
                  Reopen
                </Button>
              </>
            ) : (
              <>
                <Textarea
                  placeholder="What was done (optional)"
                  value={note}
                  onChange={(ev) => setNote(ev.target.value)}
                  aria-label="Resolution note"
                />
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="all-occ"
                    checked={all}
                    onCheckedChange={(c) => setAll(c === true)}
                  />
                  <Label htmlFor="all-occ" className="font-normal">
                    Resolve all {e.occurrences.count} occurrences
                  </Label>
                </div>
                <Button onClick={() => resolve.mutate()} disabled={resolve.isPending}>
                  Mark resolved
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Block({ title, value }: { title: string; value: unknown }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <pre className="max-h-96 overflow-auto rounded bg-muted p-3 text-xs whitespace-pre-wrap">
          {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}
